import {UUID} from '@nh/angular-tses';
import {ROLE_METADATA} from '../../permissions/role-metadata';
import {DepartureRole} from '../../permissions/types';
import {DasqlOperation} from './enums/dasql-operation';
import {EqualityRelation} from './enums/equality-relation';
import {SearchColumn} from './enums/search-column';
import {ConditionDasqlNode} from './nodes/condition-dasql-node';
import {DasqlNode} from './nodes/dasql-node';
import {MultipleLeafOperationDasqlNode} from './nodes/multiple-leaf-operation-dasql-node';
import {SingleLeafOperationDasqlNode} from './nodes/single-leaf-operation-dasql-node';

/*
 * Implementations of abstract Node classes.
 * Required to instantiate the different kinds of node as they are abstract classes.
 * Prevents usage outside of this factory class, any instantiations of these node
 * classes should be made through this factory's static methods.
 */
class ConditionDasqlNodeImpl extends ConditionDasqlNode {}
class MultipleLeafOperationDasqlNodeImpl extends MultipleLeafOperationDasqlNode {}
class SingleLeafOperationDasqlNodeImpl extends SingleLeafOperationDasqlNode {}

export class DasqlNodeFactory {
  public static and(...children: DasqlNode[]): DasqlNode {
    return new MultipleLeafOperationDasqlNodeImpl(DasqlOperation.AND, children);
  }

  public static or(...children: DasqlNode[]): DasqlNode {
    return new MultipleLeafOperationDasqlNodeImpl(DasqlOperation.OR, children);
  }

  public static not(child: DasqlNode): DasqlNode {
    return new SingleLeafOperationDasqlNodeImpl(DasqlOperation.NOT, child);
  }

  public static condition(column: SearchColumn, relation: EqualityRelation, arg: any): DasqlNode {
    return new ConditionDasqlNodeImpl(column, relation, String(arg));
  }

  public static role(userId: UUID, role?: DepartureRole): DasqlNode {
    if (role) {
      const rel: EqualityRelation = ROLE_METADATA[role].multi ? EqualityRelation.CONTAINS : EqualityRelation.EQUAL;
      return this.condition(this.roleToColumn(role), rel, userId.value);
    }

    return this.or(
      this.condition(SearchColumn.ASSIGNEE_UUID, EqualityRelation.EQUAL, userId.value),
      this.condition(SearchColumn.DESIGNERS_UUIDS, EqualityRelation.CONTAINS, userId.value),
      this.condition(SearchColumn.PROPOSER_UUID, EqualityRelation.EQUAL, userId.value),
      this.condition(SearchColumn.PROJECT_MANAGER_UUID, EqualityRelation.EQUAL, userId.value),
      this.condition(SearchColumn.SSP_UUID, EqualityRelation.EQUAL, userId.value),
      this.condition(SearchColumn.TECHNICAL_SPECIALIST_UUID, EqualityRelation.EQUAL, userId.value),
      this.condition(SearchColumn.ADDITIONAL_TECHNICAL_REVIEWERS_UUIDS, EqualityRelation.CONTAINS, userId.value),
      this.condition(SearchColumn.AUTHORISING_SIGNATORY_UUID, EqualityRelation.EQUAL, userId.value)
    );
  }

  private static roleToColumn(role: DepartureRole): SearchColumn {
    switch (role) {
      case DepartureRole.ASSIGNEE:
        return SearchColumn.ASSIGNEE_UUID;
      case DepartureRole.DESIGNERS:
        return SearchColumn.DESIGNERS_UUIDS;
      case DepartureRole.PROPOSER:
        return SearchColumn.PROPOSER_UUID;
      case DepartureRole.PROJECT_MANAGER:
        return SearchColumn.PROJECT_MANAGER_UUID;
      case DepartureRole.TECHNICAL_SPECIALIST:
        return SearchColumn.TECHNICAL_SPECIALIST_UUID;
      case DepartureRole.SSP:
        return SearchColumn.SSP_UUID;
      case DepartureRole.AUTHORISING_SIGNATORY:
        return SearchColumn.AUTHORISING_SIGNATORY_UUID;
      case DepartureRole.ADDITIONAL_TECHNICAL_REVIEWERS:
        return SearchColumn.ADDITIONAL_TECHNICAL_REVIEWERS_UUIDS;
    }
  }

  private constructor() {}
}
